import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AcademyTraining } from '../../services/medewerkerApi';
import { AcademyTegel } from './AcademyTegel';

/**
 * Horizontale, scrolbare categorie-rij (swiper) met pijlknoppen op desktop.
 * `accent` kleurt het streepje naast de rijtitel (rijkleur uit de spec).
 */
export const AcademyRij: React.FC<{
  id: string;
  titel: string;
  subtitel?: string;
  emoji?: string;
  accent?: string;
  trainingen: AcademyTraining[];
  onOpen: (training: AcademyTraining) => void;
}> = ({ id, titel, subtitel, emoji, accent, trainingen, onOpen }) => {
  const { t } = useTranslation('medewerker');
  const scroller = useRef<HTMLDivElement>(null);
  const [rand, setRand] = useState({ links: false, rechts: false });

  const meet = () => {
    const el = scroller.current;
    if (!el) return;
    setRand({ links: el.scrollLeft > 4, rechts: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };

  useEffect(() => {
    meet();
    window.addEventListener('resize', meet);
    return () => window.removeEventListener('resize', meet);
  }, [trainingen.length]);

  const schuif = (richting: 1 | -1) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: richting * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  return (
    <section aria-labelledby={`academy-rij-${id}`} className="relative">
      <div className="mb-2.5 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 id={`academy-rij-${id}`} className="flex items-center gap-2 text-lg font-bold text-gray-900">
            {accent && <span className="h-5 w-1 rounded-full" style={{ backgroundColor: accent }} aria-hidden="true" />}
            {emoji && <span aria-hidden="true">{emoji}</span>}
            <span className="truncate">{titel}</span>
          </h2>
          {subtitel && <p className="mt-0.5 text-xs text-gray-500">{subtitel}</p>}
        </div>
        <div className="hidden shrink-0 gap-1.5 sm:flex">
          <button
            type="button"
            onClick={() => schuif(-1)}
            disabled={!rand.links}
            aria-label={t('academy.rij.vorige', { defaultValue: 'Vorige' })}
            className="rounded-full border border-gray-300 bg-white p-1.5 text-gray-900 transition hover:bg-gray-100 disabled:opacity-25"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => schuif(1)}
            disabled={!rand.rechts}
            aria-label={t('academy.rij.volgende', { defaultValue: 'Volgende' })}
            className="rounded-full border border-gray-300 bg-white p-1.5 text-gray-900 transition hover:bg-gray-100 disabled:opacity-25"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        ref={scroller}
        onScroll={meet}
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-3 pt-1 [scrollbar-width:none] sm:gap-4 [&::-webkit-scrollbar]:hidden"
      >
        {trainingen.map((tr) => (
          <AcademyTegel key={tr.id} training={tr} onOpen={onOpen} className="w-[82vw] max-w-[21rem] sm:w-[20rem] lg:w-[21rem]" />
        ))}
      </div>
    </section>
  );
};

export default AcademyRij;
